import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import confetti from 'canvas-confetti';
import { Flame, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinancialData, CheckInEvent } from '../../contexts/FinancialDataContext';
import { STREAK_MILESTONES } from '../../utils/streak';

const MILESTONE_COPY: Record<number, string> = {
  4: "You've checked in on your finances every week for a month. That's how lasting habits start.",
  8: "You've kept your finances up to date every week for two months straight.",
  12: "You've checked in on your finances every week for three months. This is a real habit now.",
  26: "You've kept your finances up to date every single week for six months.",
  52: "You've checked in on your finances every week for a full year, without missing a beat.",
  104: "You've checked in on your finances every week for two years. Incredible consistency.",
};

const WEEKLY_TOAST_MS = 4500;

// Development only: /dashboard?streakPreview=12 shows that celebration on load.
// Read at module load, before the app rewrites the URL.
const DEV_PREVIEW_STREAK = process.env.NODE_ENV === 'development'
  ? Number(new URLSearchParams(window.location.search).get('streakPreview')) || 0
  : 0;

const fireConfetti = () => {
  const colors = ['#f59e0b', '#fb923c', '#8455ef', '#6b38d4', '#fcd34d'];
  const end = Date.now() + 1200;
  const frame = () => {
    confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  confetti({ particleCount: 120, spread: 90, origin: { y: 0.55 }, colors });
  frame();
};

export const StreakCelebration: React.FC = () => {
  const { darkMode } = useTheme();
  const { checkInEvent: liveEvent, dismissCheckInEvent: dismissLive } = useFinancialData();
  const [previewEvent, setPreviewEvent] = useState<CheckInEvent | null>(
    DEV_PREVIEW_STREAK ? { id: 0, streak: DEV_PREVIEW_STREAK, firstEver: DEV_PREVIEW_STREAK === 1 } : null
  );
  const checkInEvent = previewEvent ?? liveEvent;
  const dismissCheckInEvent = useCallback(
    () => (previewEvent ? setPreviewEvent(null) : dismissLive()),
    [previewEvent, dismissLive]
  );
  const isMilestone = !!checkInEvent && STREAK_MILESTONES.includes(checkInEvent.streak);

  useEffect(() => {
    if (!checkInEvent) return;
    if (isMilestone) {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) fireConfetti();
      return;
    }
    const timer = setTimeout(dismissCheckInEvent, WEEKLY_TOAST_MS);
    return () => clearTimeout(timer);
  }, [checkInEvent, isMilestone, dismissCheckInEvent]);

  if (!checkInEvent) return null;

  if (isMilestone) {
    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={dismissCheckInEvent}>
        <div
          role="dialog"
          aria-label={`${checkInEvent.streak} weeks in a row`}
          onClick={e => e.stopPropagation()}
          className={`relative w-full max-w-sm rounded-3xl p-8 text-center shadow-2xl ${
            darkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'
          }`}
        >
          <button
            type="button"
            onClick={dismissCheckInEvent}
            aria-label="Close"
            className={`absolute top-4 right-4 p-1 rounded-lg ${darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
          >
            <X size={18} />
          </button>
          <div className="mx-auto mb-5 h-20 w-20 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <Flame size={44} className="text-white fill-white/40" />
          </div>
          <p className={`text-xs font-bold uppercase tracking-[0.18em] mb-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
            Weekly check-in streak
          </p>
          <p className={`text-3xl font-black tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {checkInEvent.streak} weeks in a row
          </p>
          <p className={`mt-2 leading-relaxed text-balance ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            {MILESTONE_COPY[checkInEvent.streak]}
          </p>
          <button
            type="button"
            onClick={dismissCheckInEvent}
            className="mt-6 w-full rounded-xl py-3 font-bold text-white bg-gradient-to-br from-[#6b38d4] to-[#8455ef] shadow-lg shadow-violet-500/20 hover:brightness-110 transition"
          >
            Keep it going
          </button>
        </div>
      </div>,
      document.body
    );
  }

  const message = checkInEvent.firstEver
    ? 'Your check-in streak has started. Update your finances once a week to keep it going.'
    : `You've checked in on your finances ${checkInEvent.streak} weeks in a row. Nice work staying current.`;

  return createPortal(
    <div
      role="status"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 pl-3 pr-4 py-3 rounded-2xl shadow-xl text-white bg-gradient-to-br from-amber-500 to-orange-600 max-w-[calc(100vw-2rem)]"
    >
      <span className="h-8 w-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
        <Flame size={18} className="text-white fill-white/40" />
      </span>
      <span className="text-sm font-semibold text-balance">{message}</span>
      <button type="button" onClick={dismissCheckInEvent} aria-label="Dismiss" className="ml-1 text-white/80 hover:text-white">
        <X size={16} />
      </button>
    </div>,
    document.body
  );
};
